/**
 * Hardcoded starter email/SMS templates offered in the visual notification
 * editor. Authors pick one as a starting point — a ready header + footer and a
 * clearly marked content area — then customise the content (edit text, add
 * images, insert variables) rather than building an email from a blank page.
 *
 * The HTML is deliberately email-safe: table-based layout with inline styles,
 * so it renders consistently across mail clients. Variables use Freemarker
 * (`${…}`) so they survive the visual editor's chip round-trip.
 *
 * Each email starter is a COMPLETE HTML document (doctype + `<head>` + `<body>`)
 * rather than a bare body fragment, so the raw HTML editor shows the full
 * document an author would send and the visual editor renders it with the
 * document's own `<head>` styles.
 */

export type NotificationStarterChannel = 'email' | 'sms';

export interface NotificationStarterTemplate {
  id: string;
  name: string;
  description: string;
  channel: NotificationStarterChannel;
  /** Email subject suggestion (email starters only). */
  subject?: string;
  /** Freemarker + HTML body for email, or plain text for SMS. */
  content: string;
}

const BRAND = '#155eef';
const INK = '#344054';
const MUTED = '#667085';
const BORDER = '#eaecf0';

/** A static `<title>` for the document `<head>`. The visible header bar can use
    Freemarker, but the head `<title>` is metadata that the visual editor keeps
    outside the round-tripped body, so Freemarker there would be dropped on save.
    We strip any Freemarker and fall back to a generic label. */
function documentTitle(title: string): string {
  const plain = title.replace(
    /<#--[\s\S]*?-->|<\/?[#@][^>]*>|\$\{[^}]*\}/g,
    ''
  );
  return plain.trim() || 'IremboGov notification';
}

/** A shared, email-safe shell rendered as a COMPLETE HTML document: doctype and
    a `<head>` (charset, viewport, title and a base reset) around a centred 600px
    card with a coloured header bar and a muted footer. `body` is the
    customisable content region. Layout still relies on inline styles so it
    survives mail clients that strip `<head>` CSS. */
function shell(title: string, body: string): string {
  return [
    `<!doctype html>`,
    `<html lang="en">`,
    `<head>`,
    `<meta charset="utf-8">`,
    `<meta name="viewport" content="width=device-width, initial-scale=1.0">`,
    `<title>${documentTitle(title)}</title>`,
    `<style>`,
    `body{margin:0;padding:0;background:#f2f4f7;`,
    `font-family:Arial,Helvetica,sans-serif;color:${INK};}`,
    `table{border-collapse:collapse;}`,
    `img{border:0;line-height:100%;max-width:100%;height:auto;}`,
    `a{color:${BRAND};}`,
    `</style>`,
    `</head>`,
    `<body>`,
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f2f4f7;padding:24px 0">`,
    `<tr><td align="center">`,
    `<table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#ffffff;border:1px solid ${BORDER};border-radius:12px;overflow:hidden;font-family:Arial,Helvetica,sans-serif;color:${INK}">`,
    `<tr><td style="background:${BRAND};padding:20px 28px;color:#ffffff;font-size:18px;font-weight:bold">${title}</td></tr>`,
    `<tr><td style="padding:28px">`,
    body,
    `</td></tr>`,
    `<tr><td style="padding:20px 28px;border-top:1px solid ${BORDER};color:${MUTED};font-size:12px;line-height:1.6">`,
    `<p style="margin:0">This is an automated message from \${serviceName}. Please do not reply to this email.</p>`,
    `<p style="margin:8px 0 0">&copy; \${organizationAcronym} · IremboGov</p>`,
    `</td></tr>`,
    `</table></td></tr></table>`,
    `</body>`,
    `</html>`,
  ].join('');
}

function paragraph(text: string): string {
  return `<p style="margin:0 0 16px;font-size:14px;line-height:1.6">${text}</p>`;
}

function button(label: string, hrefVar: string): string {
  return [
    `<table role="presentation" cellpadding="0" cellspacing="0" style="margin:8px 0 20px">`,
    `<tr><td style="background:${BRAND};border-radius:8px">`,
    `<a href="${hrefVar}" style="display:inline-block;padding:12px 22px;color:#ffffff;font-size:14px;font-weight:bold;text-decoration:none">${label}</a>`,
    `</td></tr></table>`,
  ].join('');
}

export const NOTIFICATION_STARTER_TEMPLATES: NotificationStarterTemplate[] = [
  {
    id: 'blank-branded',
    name: 'Blank (branded shell)',
    description: 'Header and footer only — add your own content.',
    channel: 'email',
    subject: 'Update on your ${serviceName} application',
    content: shell(
      '${serviceName}',
      paragraph('Dear ${applicantName},') +
        paragraph('Write your message here.')
    ),
  },
  {
    id: 'application-received',
    name: 'Application received',
    description:
      'Confirms an application was submitted, with a tracking number.',
    channel: 'email',
    subject: 'We received your application ${applicationNumber}',
    content: shell(
      'Application received',
      paragraph('Dear ${applicantName},') +
        paragraph(
          'We have received your application for <b>${serviceName}</b>. Your application number is <b>${applicationNumber}</b>.'
        ) +
        paragraph('Current status: <b>${applicationState}</b>.') +
        button('Track your application', '${loginLink}') +
        paragraph('Thank you for using IremboGov.')
    ),
  },
  {
    id: 'payment-reminder',
    name: 'Payment reminder',
    description: 'Reminds the applicant to pay before an expiry date.',
    channel: 'email',
    subject: 'Payment pending for ${applicationNumber}',
    content: shell(
      'Payment pending',
      paragraph('Dear ${applicantName},') +
        paragraph(
          'A payment of <b>${formattedPrice}</b> is pending for application <b>${applicationNumber}</b>.'
        ) +
        paragraph(
          'Please complete your payment before <b>${paymentExpiration}</b> using bill reference <b>${billReferenceNumber}</b>.'
        ) +
        button('Pay now', '${loginLink}')
    ),
  },
  {
    id: 'sms-status',
    name: 'Status update (SMS)',
    description: 'Short SMS notifying an application status change.',
    channel: 'sms',
    content:
      'IremboGov: Your application ${applicationNumber} is now ${applicationState}. Track it at ${loginLink}',
  },
];

export function starterTemplatesForChannel(
  channel: NotificationStarterChannel
): NotificationStarterTemplate[] {
  return NOTIFICATION_STARTER_TEMPLATES.filter(
    template => template.channel === channel
  );
}

export function starterTemplateById(
  id: string | null | undefined
): NotificationStarterTemplate | null {
  if (!id) return null;
  return (
    NOTIFICATION_STARTER_TEMPLATES.find(template => template.id === id) || null
  );
}
